import get from 'lodash/get';
import size from 'lodash/size';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useAppSelector } from '../../app/redux/hooks';
import { FieldData, FieldDependency, FieldInfo } from '../../app/user/profile/profileUtils';
import UserserviceField from './UserserviceField';

type Props = {
  field: FieldInfo;
  userData: FieldData;
  methods: UseFormReturn;
  requiredDependentsOnDisplay?: boolean;
  booleanAsCheckbox?: boolean;
  displayLabel?: boolean;
};

/**
 * Renders a field and their dependents
 * @return {JSX}
 */
const DependencyField = ({
  field,
  userData,
  methods,
  requiredDependentsOnDisplay = false,
  booleanAsCheckbox = false,
  displayLabel = true
}: Props) => {
  const profileDetails = useAppSelector((state) => state.profile?.profile ?? null);
  const baseField = (
    <div key={field.name + '_div'}>
      <UserserviceField
        methods={methods}
        key={field.name + '_field'}
        field={field}
        userData={userData[field.name]}
        booleanAsCheckbox={booleanAsCheckbox}
        displayLabel={displayLabel}
      />
    </div>
  );

  if (size(field.fieldDependents) == 0) {
    return baseField;
  }

  const fieldValue = methods.watch(field.name);

  const dependentFields = field.fieldDependents.map((dependent: FieldDependency) => {
    const dependentField = get(
      profileDetails,
      'fieldGroups.' + dependent.fieldDependentGroups[0] + '.fields.' + dependent.fieldDependentName
    );
    const shouldShow = dependent.values.includes(String(fieldValue));

    // force required on fields from override, without mutating state
    const fieldToDisplay = { ...dependentField };
    if (requiredDependentsOnDisplay) {
      fieldToDisplay.required = true;
    }

    return (
      <React.Fragment key={dependentField.name + '_dependency'}>
        {shouldShow && (
          <div className="mt-4">
            <DependencyField
              methods={methods}
              field={fieldToDisplay}
              userData={userData}
              requiredDependentsOnDisplay={requiredDependentsOnDisplay}
              booleanAsCheckbox={booleanAsCheckbox}
              displayLabel={displayLabel}
            />
          </div>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {baseField}
      {dependentFields}
    </>
  );
};

export default DependencyField;
